import React from 'react'
import { generatePath } from 'react-router-dom'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import { FormField, FormReadonlyField, FormTextareaField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { Spinner } from '@/components/ui/spinner'
import { Alert } from 'antd'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { TransactionDto } from 'api/Transaction/Types/Transaction'
import { TransactionNoteDto } from 'api/Transaction/Types/TransactionNoteDto'
import { TransactionType } from 'api/Wallet/Types/TransactionTypeDto'
import { UpdateWalletTradingAccountTransfer } from 'api/Wallet/Types/UpdateWalletTradingAccountTransfer'
import { WalletDto } from 'api/Wallet/Types/Wallet'
import { MainTemplate } from 'components/Templates/MainTemplate'
import { TransactionMenu } from 'components/TransactionMenu/TransactionMenu'
import { Form, FormikProps, withFormik } from 'formik'
import { formatLocalDateUTC } from 'utils/date'
import { links } from 'utils/featureToggles'
import { FormSubmitValues } from 'utils/formValidation'
import { hasPermission } from 'utils/permissionsValidator'
import {
  isNotAllowedToChangeState,
  isTransactionPendingStatus,
} from 'utils/transaction/status.index'
import { getCreditCreditOrFirstOneWallet, getTradingAccount } from 'utils/wallet'

import { TransactionDetailsTab } from '../DetailTabs'
import { filterTransactionNotes, NoteTextList } from '../NoteList'
import { ContentField } from '../TransactionDetailsFieldHelpers'

interface TransactionDetailsPlatformTransferFormValues {
  id: string
  referenceId: string
  amount: number
  comment: string | null
  wallet?: WalletDto | { id: string; name: string }
}

const TransactionDetailsPlatformTransferFormUI: React.FC<
  FormikProps<TransactionDetailsPlatformTransferFormValues> & OuterProps
> = (props) => {
  const {
    resetForm,
    permissions,
    setAuditLogModal,
    transaction,
    notes,
    isEdit,
    setEdit,
    setCustomizeModal,
  } = props
  const [reversedTransaction] = transaction.reversedTransactions || []

  const handleSubmitForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    props.handleSubmit()
  }

  const cancel = () => {
    setEdit(false)
    resetForm()
  }

  const tradingAccount = getTradingAccount(props.transaction.tradingAccountTransactions || [])

  return (
    <MainTemplate>
      <div className='px-4 md:px-6'>
        <Form onSubmit={handleSubmitForm}>
          <div className='flex flex-col gap-4'>
            {notes.length > 0 && (
              <Alert
                type='error'
                message={
                  <>
                    There are notes related to the client(s) involved in this transaction. To view
                    please
                    <Button
                      type='button'
                      onClick={() => props.setActiveTab(TransactionDetailsTab.NOTES)}
                      variant='link'
                    >
                      Click Here
                    </Button>
                  </>
                }
              />
            )}

            <DataGrid
              rightAddon={
                <ButtonGroup>
                  {!isEdit ? (
                    <Button
                      type='button'
                      onClick={() => setEdit(true)}
                      disabled={
                        !hasPermission(permissions, PermissionsEnum.CanEditTransactions) ||
                        isTransactionPendingStatus(transaction.state.id) ||
                        isNotAllowedToChangeState(transaction.state.id)
                      }
                      data-test='edit-button'
                    >
                      Edit
                    </Button>
                  ) : (
                    <>
                      <Button type='button' onClick={cancel} variant='outline'>
                        Cancel
                      </Button>
                      <Button type='submit' disabled={props.isSubmitting}>
                        Save
                        {props.isSubmitting && <Spinner data-icon='inline-start' />}
                      </Button>
                    </>
                  )}
                  <TransactionMenu
                    permissions={permissions}
                    handleAuditLog={() => setAuditLogModal(true)}
                    handleCustomize={() => setCustomizeModal(true)}
                    isAdminCustomizationAvailable
                  />
                </ButtonGroup>
              }
            >
              <DataColumn>
                <DataCard title='Transaction Overview'>
                  <FormReadonlyField label='Type' value={transaction.type.name} />
                  <FormReadonlyField
                    label='Date'
                    value={formatLocalDateUTC(transaction.createdDate, true)?.toString()}
                  />
                  {props.client && (
                    <FormReadonlyField
                      label='Client'
                      value={props.client.name}
                      link={generatePath(links.recordsClients.detailsUrl as string, {
                        id: props.client.id,
                        slug: 'details',
                      })}
                    />
                  )}
                  <FormField label='Amount' name='amount' type='number' readOnly={!isEdit} />
                  {filterTransactionNotes(notes).length > 0 && (
                    <ContentField label='Transaction Notes'>
                      <div className='w-full'>
                        <NoteTextList transactionNotes={filterTransactionNotes(notes)} />
                      </div>
                    </ContentField>
                  )}
                  <FormTextareaField
                    label='Client Comment'
                    name='comment'
                    readOnly={!isEdit}
                    rows={3}
                    maxLength={130}
                  />
                  {transaction.reverseTransaction && (
                    <FormReadonlyField
                      label='Reverse Transaction'
                      value={transaction.reverseTransaction.name}
                      link={generatePath('/transactions/:id', {
                        id: transaction.reverseTransaction.id,
                      })}
                    />
                  )}
                  {reversedTransaction && (
                    <FormReadonlyField
                      label='Reversed Transaction'
                      value={reversedTransaction.name}
                      link={generatePath('/transactions/:id', {
                        id: reversedTransaction.id,
                      })}
                    />
                  )}
                </DataCard>
              </DataColumn>

              <DataColumn>
                <DataCard title='Transaction Details'>
                  {props.values.wallet && (
                    <FormReadonlyField
                      label='Wallet'
                      value={props.values.wallet.name}
                      link={generatePath(links.recordsWallets.detailsUrl as string, {
                        id: props.values.wallet.id,
                        slug: 'details',
                      })}
                    />
                  )}
                  {tradingAccount?.tradingAccount && (
                    <FormReadonlyField
                      label='Trading Account'
                      value={tradingAccount.tradingAccount.name}
                      link={generatePath(links.recordsTradingAccounts.detailsUrl as string, {
                        id: tradingAccount.tradingAccount.id,
                        slug: 'details',
                      })}
                    />
                  )}
                </DataCard>
              </DataColumn>
            </DataGrid>
          </div>
        </Form>
      </div>
    </MainTemplate>
  )
}

function dataToSubmit(
  values: TransactionDetailsPlatformTransferFormValues
): FormSubmitValues<UpdateWalletTradingAccountTransfer> {
  return {
    comment: values.comment,
    referenceId: values.referenceId,
    transactionId: values.id,
    amount: values.amount,
  }
}

export interface TransactionDetailsPlatformTransferGeneralProps {
  transaction: TransactionDto
  client: { id: string; name: string } | undefined
  permissions: PermissionDto[]

  setActiveTab: (tabName: string) => void
  onSubmit(values: FormSubmitValues<UpdateWalletTradingAccountTransfer>, typeId: number): void
  setAuditLogModal(value: boolean): void
  setCustomizeModal(value: boolean): void
}

interface OuterProps extends TransactionDetailsPlatformTransferGeneralProps {
  notes: TransactionNoteDto[]
  isEdit: boolean

  setEdit(val: boolean): void
}

export const TransactionDetailsPlatformTransferGeneralForm = withFormik<
  OuterProps,
  TransactionDetailsPlatformTransferFormValues
>({
  mapPropsToValues: ({ transaction }) => {
    // if transaction is type Negative Equity Refund, we bind from different place
    if (
      transaction.type.id === TransactionType.NegativeEquityRefund &&
      !!transaction.tradingAccountTransactions?.length
    ) {
      return {
        id: transaction.id,
        referenceId: transaction.referenceId,
        comment: transaction.comment,
        amount: transaction.tradingAccountTransactions[0].amount || 0,
        wallet: transaction.tradingAccountTransactions[0].wallet,
      }
    }
    return {
      id: transaction.id,
      referenceId: transaction.referenceId,
      comment: transaction.comment,
      amount: getCreditCreditOrFirstOneWallet(transaction.walletTransactions)?.amount as number,
      wallet: getCreditCreditOrFirstOneWallet(transaction.walletTransactions)?.wallet,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values), props.transaction.type.id)
      props.setEdit(false)
    } catch (error) {
      console.error(error)
      props.setEdit(true)
    } finally {
      setSubmitting(false)
    }
  },
  enableReinitialize: true,
})(TransactionDetailsPlatformTransferFormUI)
